// localStorage lineup cache for Phase 1. Pre-pulled record-game lineups
// live on the stats object; THIS cache is for the lazy-fetch path that
// activates when a user clicks a non-record game (and, today, also acts
// as a memory layer for the pre-pull step so re-imports of the same
// league don't refetch from Yahoo).
//
// Key shape: `almanac_lineup_v1__${league_id}__${year}__W${week}__${owner_key}`
//
// `owner_key` is whatever stable identifier the platform uses — Sleeper
// roster_id, Yahoo team_key — passed through by the caller. Both formats
// are URL-safe so no encoding needed.
//
// TTL: 24h since fetched. Past-season lineups are immutable but we still
// expire so the cache cleans itself if storage drift happens.
//
// Size cap: 5MB per league. We keep an index entry per league prefix and
// evict oldest by writtenAt on each write that pushes the league over budget.

const PREFIX = "almanac_lineup_v1__";
const TTL_MS = 24 * 60 * 60 * 1000;
const PER_LEAGUE_BUDGET = 5 * 1024 * 1024;

function buildKey(leagueId, year, week, ownerKey) {
  return `${PREFIX}${leagueId}__${year}__W${week}__${ownerKey}`;
}

function safeJsonParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

export function getLineup({ leagueId, year, week, ownerKey }) {
  if (typeof localStorage === "undefined") return null;
  const key = buildKey(leagueId, year, week, ownerKey);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const entry = safeJsonParse(raw);
  if (!entry || !entry.writtenAt || !entry.lineup) return null;
  if (Date.now() - entry.writtenAt > TTL_MS) {
    localStorage.removeItem(key);
    return null;
  }
  return entry.lineup;
}

export function setLineup({ leagueId, year, week, ownerKey, lineup }) {
  if (typeof localStorage === "undefined") return;
  const key = buildKey(leagueId, year, week, ownerKey);
  const payload = JSON.stringify({ writtenAt: Date.now(), lineup });
  try {
    localStorage.setItem(key, payload);
  } catch {
    // Storage full — evict league entries until our write fits.
    evictLeagueToBudget(leagueId, payload.length);
    try { localStorage.setItem(key, payload); } catch { /* ignore */ }
    return;
  }
  // Soft-budget check after success.
  evictLeagueToBudget(leagueId, 0);
}

// Walk all keys for this league and evict oldest writtenAt until under
// PER_LEAGUE_BUDGET. Best-effort - this runs synchronously so we keep
// the scan small (only entries that match the league prefix).
function evictLeagueToBudget(leagueId, extraBytes) {
  if (typeof localStorage === "undefined") return;
  const leaguePrefix = `${PREFIX}${leagueId}__`;
  const entries = [];
  let totalBytes = extraBytes || 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(leaguePrefix)) continue;
    const v = localStorage.getItem(k);
    if (!v) continue;
    const entry = safeJsonParse(v);
    const writtenAt = entry?.writtenAt || 0;
    const bytes = k.length + v.length;
    totalBytes += bytes;
    entries.push({ key: k, writtenAt, bytes });
  }
  if (totalBytes <= PER_LEAGUE_BUDGET) return;
  entries.sort((a, b) => a.writtenAt - b.writtenAt);
  for (const e of entries) {
    if (totalBytes <= PER_LEAGUE_BUDGET) break;
    localStorage.removeItem(e.key);
    totalBytes -= e.bytes;
  }
}
